import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DialogConfig, DialogType, useDialogRouterStore } from "./use-dialog-router-store";

// Helper function to create properly typed dialog config from URL params
function createDialogConfig(
  dialogType: DialogType,
  id: string | undefined,
  params: Record<string, string>
): DialogConfig {
  const baseConfig = {
    type: dialogType,
    id,
  };

  switch (dialogType) {
    case "appointment-detail":
      return baseConfig as DialogConfig;

    case "appointment-create":
      return {
        ...baseConfig,
        params: {
          customerId: params.customerId,
          workerId: params.workerId,
          date: params.date,
          time: params.time,
          notes: params.notes,
        },
      } as DialogConfig;

    case "appointment-edit":
      return {
        ...baseConfig,
        params: {
          appointmentId: params.appointmentId,
        },
      } as DialogConfig;

    case "auto-suggestion":
      return {
        ...baseConfig,
        params: {
          query: params.query,
          context: params.context,
        },
      } as DialogConfig;

    case "customer-detail":
    case "service-detail":
      return baseConfig as DialogConfig;

    default:
      return baseConfig as DialogConfig;
  }
}

export interface UseDialogRouterReturn {
  // State
  isDialogOpen: (type: DialogType) => boolean;
  getDialogConfig: (type: DialogType) => DialogConfig | null;

  // Actions
  openDialog: (config: DialogConfig) => void;
  closeDialog: (type?: DialogType) => void;
  closeAllDialogs: () => void;
  replaceDialog: (config: DialogConfig) => void;
}

/**
 * Hook that integrates dialog state with URL parameters
 * Manages dialog state through URL search params for better UX
 */
export function useDialogRouterSimple(): UseDialogRouterReturn {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    dialogStack,
    openDialog: storeOpenDialog,
    closeDialog: storeCloseDialog,
    closeAllDialogs: storeCloseAllDialogs,
    getDialogConfig: storeGetDialogConfig,
  } = useDialogRouterStore();

  // Sync URL params with dialog state on mount and URL changes
  useEffect(() => {
    const dialogParam = searchParams.get("dialog");
    const idParam = searchParams.get("id");

    if (dialogParam) {
      const dialogType = dialogParam as DialogType;
      const urlParams = Object.fromEntries(
        Array.from(searchParams.entries()).filter(([key]) => key !== "dialog" && key !== "id")
      );
      const config = createDialogConfig(dialogType, idParam || undefined, urlParams);

      // Only open if not already open with same config
      const currentConfig = storeGetDialogConfig(dialogType);
      if (!currentConfig) {
        // Dialog doesn't exist, open it
        storeOpenDialog(config);
      } else {
        // Dialog exists, check if config is different
        const isDifferent =
          currentConfig.id !== config.id ||
          JSON.stringify(currentConfig.params || {}) !== JSON.stringify(config.params || {});

        if (isDifferent) {
          storeOpenDialog(config);
        }
      }
    } else {
      // No dialog in URL, close all dialogs only if there are any
      const currentStack = useDialogRouterStore.getState().dialogStack;
      if (currentStack.length > 0) {
        storeCloseAllDialogs();
      }
    }
  }, [searchParams, storeOpenDialog, storeCloseAllDialogs, storeGetDialogConfig]);

  const openDialog = (config: DialogConfig) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("dialog", config.type);

    if (config.id) {
      newParams.set("id", config.id);
    }

    if (config.params) {
      Object.entries(config.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          newParams.set(key, String(value));
        }
      });
    }

    navigate(`?${newParams.toString()}`, { replace: true });
  };

  const closeDialog = (type?: DialogType) => {
    if (type) {
      // For specific dialog type, we need to check if it's the current one in URL
      const currentDialogParam = searchParams.get("dialog");
      if (currentDialogParam === type) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("dialog");
        newParams.delete("id");
        navigate(`?${newParams.toString()}`, { replace: true });
      } else {
        // If it's not the current URL dialog, just close from store
        storeCloseDialog(type);
      }
    } else {
      // Close current dialog and update URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("dialog");
      newParams.delete("id");

      navigate(`?${newParams.toString()}`, { replace: true });
    }
  };

  const closeAllDialogs = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("dialog");
    newParams.delete("id");

    navigate(`?${newParams.toString()}`, { replace: true });
  };

  const replaceDialog = (config: DialogConfig) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("dialog", config.type);

    if (config.id) {
      newParams.set("id", config.id);
    } else {
      newParams.delete("id");
    }

    if (config.params) {
      Object.entries(config.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          newParams.set(key, String(value));
        }
      });
    }

    navigate(`?${newParams.toString()}`, { replace: true });
  };

  // Create reactive versions of the store methods that will trigger re-renders
  // React 19 compiler will automatically memoize these functions
  const isDialogOpen = (type: DialogType) => {
    return dialogStack.some((dialog) => dialog.type === type);
  };

  const getDialogConfig = (type: DialogType) => {
    return dialogStack.find((dialog) => dialog.type === type) || null;
  };

  return {
    isDialogOpen,
    getDialogConfig,
    openDialog,
    closeDialog,
    closeAllDialogs,
    replaceDialog,
  };
}
